/**
 * Enhanced debugging utilities for AI Market Analysis
 */

export interface DebugContext {
  sessionId: string;
  symbol: string;
  stepType: string;
  timestamp: string;
}

export interface TechnicalDataDebug {
  trend_data?: any;
  volume_data?: any;
  momentum_data?: any;
  raw_tradingview_data?: any;
}

export class AIAnalysisDebugger {
  private static logs: Array<{
    context: DebugContext;
    level: 'info' | 'warn' | 'error';
    message: string;
    data?: any;
    timestamp: string;
  }> = [];

  /**
   * Enhanced logging with context
   */
  static log(
    context: DebugContext,
    level: 'info' | 'warn' | 'error',
    message: string,
    data?: any
  ): void {
    const logEntry = {
      context,
      level,
      message,
      data,
      timestamp: new Date().toISOString()
    };

    this.logs.push(logEntry);

    // Console output with enhanced formatting
    const prefix = `[AI-Analysis][${context.stepType}][${context.symbol}]`;
    const logFunction = console[level] || console.log;
    
    if (data) {
      logFunction(`${prefix} ${message}`, data);
    } else {
      logFunction(`${prefix} ${message}`);
    }

    // Keep only last 100 logs to prevent memory issues
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }
  }

  /**
   * Debug technical data quality
   */
  static debugTechnicalData(
    context: DebugContext,
    technicalData: TechnicalDataDebug
  ): void {
    const issues: string[] = [];
    const insights: string[] = [];

    // Analyze trend data
    if (technicalData.trend_data) {
      if (!technicalData.trend_data.ema_analysis) {
        issues.push('Missing EMA analysis in trend data');
      } else {
        insights.push(`EMA trend: ${technicalData.trend_data.ema_analysis.ema_trend || 'unknown'}`);
      }

      if (!technicalData.trend_data.price_trend) {
        issues.push('Missing price trend data');
      } else {
        insights.push(`Price trend: ${technicalData.trend_data.price_trend}`);
      }
    } else {
      issues.push('No trend data available');
    }

    // Analyze momentum data
    if (technicalData.momentum_data) {
      if (technicalData.momentum_data.rsi) {
        const rsi = parseFloat(technicalData.momentum_data.rsi);
        if (rsi < 0 || rsi > 100) {
          issues.push(`Invalid RSI value: ${rsi}`);
        } else {
          insights.push(`RSI: ${rsi.toFixed(1)} (${rsi > 70 ? 'overbought' : rsi < 30 ? 'oversold' : 'neutral'})`);
        }
      } else {
        issues.push('Missing RSI in momentum data');
      }

      if (!technicalData.momentum_data.macd) {
        issues.push('Missing MACD in momentum data');
      } else {
        insights.push('MACD data available');
      }
    } else {
      issues.push('No momentum data available');
    }

    // Analyze volume data
    if (technicalData.volume_data) {
      if (!technicalData.volume_data.volume_trend) {
        issues.push('Missing volume trend data');
      } else {
        insights.push(`Volume trend: ${technicalData.volume_data.volume_trend}`);
      }
    } else {
      issues.push('No volume data available');
    }

    // Log findings
    if (issues.length > 0) {
      this.log(context, 'warn', 'Technical data quality issues detected', { issues });
    }

    if (insights.length > 0) {
      this.log(context, 'info', 'Technical data insights', { insights });
    }

    // Overall data quality score
    const totalPossibleIndicators = 6; // EMA, price trend, RSI, MACD, volume trend, etc.
    const availableIndicators = insights.length;
    const qualityScore = (availableIndicators / totalPossibleIndicators) * 100;

    this.log(context, qualityScore < 50 ? 'warn' : 'info', 
      `Technical data quality score: ${qualityScore.toFixed(1)}%`);
  }

  /**
   * Debug AI response vs expected format
   */
  static debugAIResponse(
    context: DebugContext,
    rawResponse: any,
    expectedFormat: string
  ): void {
    if (!rawResponse) {
      this.log(context, 'error', 'AI returned empty/null response');
      return;
    }

    if (context.stepType === 'TRENDS_ANALYSIS') {
      this.debugTrendsAnalysisResponse(context, rawResponse);
    } else if (context.stepType === 'AI_RECOMMENDATION') {
      this.debugRecommendationResponse(context, rawResponse);
    } else {
      this.log(context, 'info', 'AI response received', { responseKeys: Object.keys(rawResponse) });
    }
  }

  /**
   * Debug trends analysis specific response
   */
  private static debugTrendsAnalysisResponse(context: DebugContext, response: any): void {
    const timeframes = ['short', 'medium', 'long'];
    const issues: string[] = [];
    const validDirections = ['up', 'down', 'neutral'];

    timeframes.forEach(tf => {
      if (!response[tf]) {
        issues.push(`Missing ${tf} timeframe data`);
      } else {
        const tfData = response[tf];
        
        if (!validDirections.includes(tfData.direction)) {
          issues.push(`Invalid ${tf} direction: \"${tfData.direction}\" (expected: ${validDirections.join(', ')})`);
        }

        if (typeof tfData.strength !== 'number' || tfData.strength < 0 || tfData.strength > 100) {
          issues.push(`Invalid ${tf} strength: ${tfData.strength} (expected: 0-100)`);
        }

        // Log successful parsing
        this.log(context, 'info', 
          `${tf} timeframe: ${tfData.direction} (${tfData.strength}%)`);
      }
    });

    if (issues.length > 0) {
      this.log(context, 'warn', 'Trends analysis response issues', { issues });
    }

    // Check overall assessment
    if (!response.overall_assessment) {
      this.log(context, 'warn', 'Missing overall assessment in trends analysis');
    } else {
      this.log(context, 'info', 'Overall assessment available', {
        summary_length: response.overall_assessment.summary?.length || 0,
        insights_count: response.overall_assessment.keyInsights?.length || 0
      });
    }
  }

  /**
   * Debug recommendation response
   */
  private static debugRecommendationResponse(context: DebugContext, response: any): void {
    const validActions = ['BUY', 'SELL', 'HOLD'];
    const issues: string[] = [];

    if (!validActions.includes(response.action)) {
      issues.push(`Invalid action: \"${response.action}\" (expected: ${validActions.join(', ')})`);
    }

    if (typeof response.confidence !== 'number' || response.confidence < 0 || response.confidence > 100) {
      issues.push(`Invalid confidence: ${response.confidence} (expected: 0-100)`);
    }

    if (!response.reasoning || response.reasoning.length < 50) {
      issues.push(`Insufficient reasoning: ${response.reasoning?.length || 0} chars (expected: 50+)`);
    }

    if (issues.length > 0) {
      this.log(context, 'warn', 'Recommendation response issues', { issues });
    } else {
      this.log(context, 'info', `Valid recommendation: ${response.action} (${response.confidence}%)`);
    }
  }

  /**
   * Export logs for debugging
   */
  static exportLogs(sessionId?: string): Array<any> {
    if (sessionId) {
      return this.logs.filter(log => log.context.sessionId === sessionId);
    }
    return [...this.logs];
  }

  /**
   * Clear logs
   */
  static clearLogs(): void {
    this.logs = [];
    console.log('[AI-Analysis] Debug logs cleared');
  }

  /**
   * Get summary of recent issues
   */
  static getRecentIssues(lastMinutes: number = 30): Array<{ message: string; count: number; lastSeen: string }> {
    const cutoff = new Date(Date.now() - lastMinutes * 60 * 1000);
    const recentLogs = this.logs.filter(log => 
      new Date(log.timestamp) > cutoff && (log.level === 'warn' || log.level === 'error')
    );

    const issueGroups = new Map<string, { count: number; lastSeen: string }>();

    recentLogs.forEach(log => {
      const key = log.message;
      if (issueGroups.has(key)) {
        const existing = issueGroups.get(key)!;
        existing.count++;
        existing.lastSeen = log.timestamp;
      } else {
        issueGroups.set(key, { count: 1, lastSeen: log.timestamp });
      }
    });

    return Array.from(issueGroups.entries()).map(([message, data]) => ({
      message,
      ...data
    })).sort((a, b) => b.count - a.count);
  }
}

/**
 * Helper function to create debug context
 */
export function createDebugContext(
  sessionId: string,
  symbol: string,
  stepType: string
): DebugContext {
  return {
    sessionId,
    symbol,
    stepType,
    timestamp: new Date().toISOString()
  };
}
